import { useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changelogQuery, marcarNovidadesVistas } from '../api/changelog';
import { SeloTag } from './SeloTag';
import { Botao } from './UI';

/**
 * "O QUE MUDOU DESDE A SUA ÚLTIMA VISITA".
 *
 * `<dialog>` nativo, e não uma div com `role="dialog"`: o elemento nativo já
 * prende o foco dentro dele, devolve o foco ao fechar, fecha no Esc e some do
 * leitor de tela o conteúdo de trás. Reimplementar isso à mão é onde quase todo
 * modal quebra para quem navega por teclado.
 *
 * Aparece UMA vez por pessoa: ao fechar, marcamos como visto. Quem quiser rever
 * tem `/novidades`, e o rodapé do próprio popup leva para lá.
 */
export function PopupNovidades() {
  const qc = useQueryClient();
  const ref = useRef<HTMLDialogElement>(null);
  const { data } = useQuery(changelogQuery);

  const marcar = useMutation({
    mutationFn: marcarNovidadesVistas,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['changelog'] });
      await qc.invalidateQueries({ queryKey: ['eu'] });
    },
  });

  const naoVistas = data?.nao_vistas ?? [];
  const novas = (data?.entradas ?? []).filter((e) => naoVistas.includes(e.id));

  useEffect(() => {
    const dialogo = ref.current;
    if (!dialogo || novas.length === 0) return;
    if (!dialogo.open) dialogo.showModal();
  }, [novas.length]);

  if (novas.length === 0) return null;

  function fechar() {
    ref.current?.close();
    // Mesmo que a chamada falhe, a pessoa já fechou: reabrir por causa de um
    // erro de rede transformaria uma cortesia em obstáculo.
    marcar.mutate();
  }

  return (
    <dialog
      ref={ref}
      aria-labelledby="t-novidades-popup"
      className="popup-novidades"
      // Fechar no Esc dispara `cancel`; sem isto o popup voltaria na próxima
      // navegação, porque nada teria sido marcado como visto.
      onCancel={(e) => { e.preventDefault(); fechar(); }}
    >
      <div className="flex flex-col gap-4">
        <h2 id="t-novidades-popup" className="text-2xl font-bold">
          {novas.length === 1 ? 'Uma novidade para você' : `${novas.length} novidades para você`}
        </h2>

        <ul className="flex flex-col gap-4">
          {novas.slice(0, 4).map((e) => (
            <li key={e.id} className="novidade-item">
              <h3>{e.title}</h3>
              <p className="novidade-meta"><SeloTag tag={e.tag} /></p>
              <p>{e.text}</p>
            </li>
          ))}
        </ul>

        {novas.length > 4 && (
          <p className="text-[var(--color-text-muted)]">
            E mais {novas.length - 4}. Veja tudo em Novidades.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <Botao type="button" onClick={fechar}>Entendi</Botao>
          <Link to="/novidades" onClick={fechar} className="acao-link">
            Ver todas as novidades
          </Link>
        </div>
      </div>
    </dialog>
  );
}
